import {
  forwardRef,
  useId,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  type InputHTMLAttributes,
  type ReactNode,
  type KeyboardEvent,
  type ChangeEvent,
  type FocusEvent,
  type ClipboardEvent,
} from 'react';
import { cn } from '@/shared/lib/cn';
import {
  controlHeightClass,
  errorClass,
  fieldBaseClass,
  fieldBorderClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';
import type { ControlSize, FieldTone } from '@/shared/ui/core/types';
import {
  formatRawNumber,
  parseFormattedNumber,
  formatNumberValue,
  calculateCaretPosition,
} from '@/shared/ui/core/numberInputUtils';

export interface NumberInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'size' | 'value' | 'defaultValue' | 'onChange' | 'type' | 'prefix'
  > {
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  mono?: boolean;
  required?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;

  /** Giá trị số (controlled) */
  value?: number | string | null;
  /** Giá trị mặc định (uncontrolled) */
  defaultValue?: number | string | null;
  /** Callback trả về giá trị số thực (number | null) và chuỗi hiển thị */
  onValueChange?: (value: number | null, formattedValue: string) => void;
  /** Callback sự kiện onChange chuẩn của React input */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  /** Số chữ số thập phân tối đa cho phép. Mặc định: 2 */
  decimals?: number;
  /** Cho phép nhập số âm hay không. Mặc định: false (hoặc true nếu min < 0) */
  allowNegative?: boolean;
  /** Giá trị nhỏ nhất */
  min?: number;
  /** Giá trị lớn nhất */
  max?: number;
  /** Bước nhảy khi dùng phím mũi tên Lên/Xuống (mặc định: 1) */
  step?: number;
  /** Căn lề số: 'left' | 'right' (mặc định: 'right') */
  align?: 'left' | 'right';
  /** Tiền tố hiển thị (ví dụ: '₫', '$') - alias tiện lợi cho startAdornment */
  prefix?: ReactNode;
  /** Hậu tố hiển thị (ví dụ: 'kg', 'VND', '%') */
  suffix?: ReactNode;
  /** Tự động giới hạn về khoảng [min, max] khi blur (mặc định: true) */
  clampOnBlur?: boolean;
  /** Tự động bôi đen toàn bộ dữ liệu khi click vào input có sẵn data (mặc định: true) */
  autoSelect?: boolean;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      className,
      label,
      hint,
      error,
      tone,
      size = 'md',
      mono = true,
      required,
      startAdornment,
      endAdornment,
      prefix,
      suffix,
      value,
      defaultValue,
      onValueChange,
      onChange,
      decimals = 2,
      allowNegative: allowNegativeProp,
      min,
      max,
      step = 1,
      align = 'right',
      clampOnBlur = true,
      autoSelect = true,
      placeholder = '0',
      id,
      disabled,
      readOnly,
      onKeyDown,
      onBlur,
      onPaste,
      ...props
    },
    forwardedRef,
  ) {
    const autoId = useId();
    const inputId = id ?? autoId;
    const formField = useFormFieldContext();
    const effectiveError = error || (formField.inFormField ? formField.error : undefined);
    const describedBy = effectiveError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
    const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
    const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

    const allowNegative = allowNegativeProp ?? (min !== undefined && min < 0);
    const formatOpts = { decimals, allowNegative };

    // Khởi tạo displayValue
    const isControlled = value !== undefined;
    const initialRaw = isControlled ? value : defaultValue;
    const [displayVal, setDisplayVal] = useState<string>(() =>
      formatNumberValue(initialRaw, formatOpts),
    );

    // Đồng bộ khi value bên ngoài thay đổi (controlled)
    useEffect(() => {
      if (isControlled) {
        const formatted = formatNumberValue(value, formatOpts);
        setDisplayVal((prev) => {
          // Chỉ cập nhật nếu giá trị số thực sự thay đổi để tránh gián đoạn khi đang gõ
          const prevNumeric = parseFormattedNumber(prev);
          const nextNumeric = parseFormattedNumber(formatted);
          if (prevNumeric !== nextNumeric) {
            return formatted;
          }
          // Nếu prev rỗng hoặc formatted rỗng
          if (prev !== formatted && (prev === '' || formatted === '')) {
            return formatted;
          }
          return prev;
        });
      }
    }, [value, decimals, allowNegative]);

    // Giữ con trỏ không bị nhảy khi format lại
    const pendingCaretRef = useRef<number | null>(null);
    useEffect(() => {
      if (pendingCaretRef.current !== null && inputRef.current) {
        const caret = pendingCaretRef.current;
        inputRef.current.setSelectionRange(caret, caret);
        pendingCaretRef.current = null;
      }
    });

    const updateValue = (
      nextFormatted: string,
      targetInput: HTMLInputElement | null,
      caretPos?: number,
    ) => {
      setDisplayVal(nextFormatted);
      if (caretPos !== undefined) {
        pendingCaretRef.current = caretPos;
      }

      const numericVal = parseFormattedNumber(nextFormatted);
      onValueChange?.(numericVal, nextFormatted);

      if (onChange && targetInput) {
        // Tạo synthetic event chuẩn cho form handlers
        const nativeInput = targetInput;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        )?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(nativeInput, nextFormatted);
        }
        const event = new Event('input', { bubbles: true });
        nativeInput.dispatchEvent(event);
      }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const caret = e.target.selectionStart ?? raw.length;
      const rawBeforeCaret = raw.slice(0, caret);

      const nextFormatted = formatRawNumber(raw, formatOpts);
      const newCaret = calculateCaretPosition(rawBeforeCaret, nextFormatted);

      updateValue(nextFormatted, e.target, newCaret);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented || disabled || readOnly) return;

      const input = inputRef.current;
      if (!input) return;

      const { selectionStart, selectionEnd } = input;

      // Phím Backspace khi đứng ngay sau dấu phân cách hàng nghìn "."
      if (e.key === 'Backspace') {
        if (selectionStart !== null && selectionStart === selectionEnd && selectionStart > 0) {
          if (displayVal[selectionStart - 1] === '.') {
            e.preventDefault();
            // Xóa ký tự số trước dấu chấm
            const before = displayVal.slice(0, selectionStart - 2);
            const after = displayVal.slice(selectionStart);
            const rawCombined = before + after;
            const nextFormatted = formatRawNumber(rawCombined, formatOpts);
            const newCaret = calculateCaretPosition(before, nextFormatted);
            updateValue(nextFormatted, input, newCaret);
            return;
          }
        }
      }

      // Phím Delete khi đứng ngay trước dấu phân cách hàng nghìn "."
      if (e.key === 'Delete') {
        if (selectionStart !== null && selectionStart === selectionEnd && selectionStart < displayVal.length) {
          if (displayVal[selectionStart] === '.') {
            e.preventDefault();
            // Xóa ký tự số sau dấu chấm
            const before = displayVal.slice(0, selectionStart);
            const after = displayVal.slice(selectionStart + 2);
            const rawCombined = before + after;
            const nextFormatted = formatRawNumber(rawCombined, formatOpts);
            const newCaret = calculateCaretPosition(before, nextFormatted);
            updateValue(nextFormatted, input, newCaret);
            return;
          }
        }
      }

      // Hỗ trợ cả phím phẩy "," và phím chấm "." (kể cả Numpad dot) để nhập phần thập phân
      if ((e.key === '.' || e.key === ',' || e.key === 'Decimal') && decimals > 0) {
        e.preventDefault();
        if (!displayVal.includes(',')) {
          const start = selectionStart ?? displayVal.length;
          const end = selectionEnd ?? displayVal.length;
          const before = displayVal.slice(0, start);
          const after = displayVal.slice(end);
          const rawWithComma = `${before},${after}`;
          const nextFormatted = formatRawNumber(rawWithComma, formatOpts);
          const newCaret = calculateCaretPosition(`${before},`, nextFormatted);
          updateValue(nextFormatted, input, newCaret);
        } else {
          // Nếu đã có dấu phẩy rồi, di chuyển con trỏ ra ngay sau dấu phẩy
          const commaIndex = displayVal.indexOf(',');
          input.setSelectionRange(commaIndex + 1, commaIndex + 1);
        }
        return;
      }

      // Mũi tên Lên / Xuống tăng giảm theo step
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        stepBy(step);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        stepBy(-step);
        return;
      }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(e);
      if (e.defaultPrevented || disabled || readOnly) return;

      const pastedText = e.clipboardData.getData('text');
      if (!pastedText) return;

      const parsed = parseFormattedNumber(pastedText);
      if (parsed !== null) {
        e.preventDefault();
        let targetNum = parsed;
        if (clampOnBlur) {
          if (min !== undefined && targetNum < min) targetNum = min;
          if (max !== undefined && targetNum > max) targetNum = max;
        }
        const nextFormatted = formatNumberValue(targetNum, formatOpts);
        updateValue(nextFormatted, inputRef.current, nextFormatted.length);
      }
    };

    const stepBy = (delta: number) => {
      if (disabled || readOnly) return;
      const current = parseFormattedNumber(displayVal) ?? 0;
      let next = current + delta;

      if (min !== undefined && next < min) next = min;
      if (max !== undefined && next > max) next = max;

      const nextFormatted = formatNumberValue(next, formatOpts);
      updateValue(nextFormatted, inputRef.current);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      if (disabled || readOnly) return;

      const num = parseFormattedNumber(displayVal);
      if (num === null) {
        if (displayVal !== '') {
          updateValue('', inputRef.current);
        }
        return;
      }

      let clamped = num;
      if (clampOnBlur) {
        if (min !== undefined && clamped < min) clamped = min;
        if (max !== undefined && clamped > max) clamped = max;
      }

      // Chuẩn hóa chuỗi số sau khi rời khỏi input
      const normalized = formatNumberValue(clamped, formatOpts);
      if (normalized !== displayVal) {
        updateValue(normalized, inputRef.current);
      }
    };

    const effectiveStart = prefix ?? startAdornment;
    const effectiveEnd = suffix ?? endAdornment;
    const currentNumeric = parseFormattedNumber(displayVal);

    return (
      <div className="flex w-full flex-col gap-1.5">
        {label ? (
          <label htmlFor={inputId} className={cn(labelClass, 'flex items-center gap-1')}>
            <span>{label}</span>
            {required ? (
              <span className="text-danger font-bold text-sm leading-none" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>
        ) : null}

        <div
          onClick={(e) => {
            if (e.target !== inputRef.current) {
              inputRef.current?.focus();
              if (autoSelect && inputRef.current?.value) {
                inputRef.current.select();
              }
            }
          }}
          className={cn(
            'w-full rounded-input border bg-surface transition-colors flex items-center px-3 gap-1.5 cursor-text',
            fieldBorderClass[resolvedTone],
            controlHeightClass[size],
            'focus-within:border-primary',
            disabled && 'cursor-not-allowed bg-disabled-bg text-disabled-fg border-disabled-border',
            readOnly && 'bg-readonly-bg text-readonly-fg',
            className,
          )}
        >
          {effectiveStart ? (
            <span className="shrink-0 flex items-center text-text-muted select-none">
              {effectiveStart}
            </span>
          ) : null}

          <input
            ref={inputRef}
            id={inputId}
            type="text"
            inputMode={decimals > 0 ? 'decimal' : 'numeric'}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            value={displayVal}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onPaste={handlePaste}
            data-auto-select={autoSelect === false ? 'false' : undefined}
            aria-invalid={resolvedTone === 'error' || undefined}
            aria-describedby={describedBy}
            aria-valuenow={currentNumeric ?? undefined}
            aria-valuemin={min}
            aria-valuemax={max}
            role="spinbutton"
            className={cn(
              'min-w-0 flex-1 bg-transparent border-0 outline-none p-0 text-text text-[inherit] placeholder:text-text-muted',
              mono && 'font-mono tabular-nums',
              align === 'right' ? 'text-right' : 'text-left',
              disabled && 'cursor-not-allowed',
            )}
            {...props}
          />

          {effectiveEnd ? (
            <span className="shrink-0 text-[12px] font-medium text-text-secondary select-none">
              {effectiveEnd}
            </span>
          ) : null}
        </div>

        {shouldRenderError ? (
          <p id={`${inputId}-error`} className={errorClass} role="alert">
            {effectiveError}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className={hintClass}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
